<script setup>
import { ref, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  selectedBot: {
    type: Object,
    default: () => ({}),
  },
});

const store = useStore();
const { t } = useI18n();
const dialogRef = ref(null);
const isSaving = ref(false);
const entries = ref([]);

const emptyEntry = () => ({ question: '', answer: '' });

const initializeEntries = () => {
  const knowledgeBase = props.selectedBot?.bot_config?.knowledge_base || [];
  entries.value = knowledgeBase.length
    ? knowledgeBase.map(entry => ({ ...entry }))
    : [emptyEntry()];
};

const addEntry = () => {
  entries.value.push(emptyEntry());
};

const removeEntry = index => {
  entries.value.splice(index, 1);
};

const handleSave = async () => {
  isSaving.value = true;
  try {
    await store.dispatch('agentBots/update', {
      id: props.selectedBot.id,
      data: {
        name: props.selectedBot.name,
        description: props.selectedBot.description,
        outgoing_url:
          props.selectedBot.outgoing_url ||
          props.selectedBot.bot_config?.webhook_url,
        bot_type: props.selectedBot.bot_type,
        bot_config: {
          ...props.selectedBot.bot_config,
          knowledge_base: entries.value.filter(
            entry => entry.question.trim() || entry.answer.trim()
          ),
        },
      },
    });
    useAlert(t('AGENT_BOTS.KNOWLEDGE_BASE.API.SUCCESS_MESSAGE'));
    dialogRef.value.close();
  } catch (error) {
    useAlert(t('AGENT_BOTS.KNOWLEDGE_BASE.API.ERROR_MESSAGE'));
  } finally {
    isSaving.value = false;
  }
};

watch(() => props.selectedBot, initializeEntries, {
  immediate: true,
  deep: true,
});

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="2xl"
    :title="t('AGENT_BOTS.KNOWLEDGE_BASE.TITLE')"
    :description="t('AGENT_BOTS.KNOWLEDGE_BASE.DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <div class="flex flex-col gap-4 overflow-y-auto max-h-[60vh]">
      <div
        v-for="(entry, index) in entries"
        :key="index"
        class="flex flex-col gap-2 p-3 border rounded-md border-n-weak"
      >
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-n-slate-12">
            {{
              t('AGENT_BOTS.KNOWLEDGE_BASE.ENTRY_LABEL', { index: index + 1 })
            }}
          </span>
          <NextButton
            icon="i-lucide-trash-2"
            xs
            ruby
            faded
            type="button"
            @click="removeEntry(index)"
          />
        </div>
        <Input
          v-model="entry.question"
          :label="t('AGENT_BOTS.KNOWLEDGE_BASE.QUESTION.LABEL')"
          :placeholder="t('AGENT_BOTS.KNOWLEDGE_BASE.QUESTION.PLACEHOLDER')"
        />
        <TextArea
          v-model="entry.answer"
          :label="t('AGENT_BOTS.KNOWLEDGE_BASE.ANSWER.LABEL')"
          :placeholder="t('AGENT_BOTS.KNOWLEDGE_BASE.ANSWER.PLACEHOLDER')"
        />
      </div>

      <NextButton
        icon="i-lucide-circle-plus"
        faded
        slate
        type="button"
        :label="t('AGENT_BOTS.KNOWLEDGE_BASE.ADD_ENTRY')"
        @click="addEntry"
      />
    </div>

    <div class="flex items-center justify-end w-full gap-2 px-0 py-2">
      <NextButton
        faded
        slate
        type="button"
        :label="t('AGENT_BOTS.FORM.CANCEL')"
        @click="dialogRef.close()"
      />
      <NextButton
        type="button"
        :label="t('AGENT_BOTS.KNOWLEDGE_BASE.SAVE')"
        :is-loading="isSaving"
        @click="handleSave"
      />
    </div>
  </Dialog>
</template>
